import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState, useRef } from "react";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { registerInterest, lookupPostcode, confirmArrival } from "@/lib/shifts.functions";
import { getPayRate } from "@/lib/pay-rates";
import { confirmShiftCompletion, cancelShift } from "@/lib/payments.functions";
import { updateProfileDetails } from "@/lib/reviews.functions";
import { getStripeEnvironment } from "@/lib/stripe";
import { flowBreadcrumb, setFlowSubject } from "@/lib/breadcrumbs";
import { reportCancellationFailure } from "@/lib/cancellation-error";
import { submitApplicationCv } from "@/lib/applications.functions";
import { ReserveHeader } from "@/components/ReserveHeader";
import { FoundingMemberBadge } from "@/components/FoundingMemberBadge";
import { RtwCheckCard } from "@/components/RtwCheckCard";
import { VeriffRtwStatusPanel } from "@/components/VeriffRtwStatusPanel";
import { DisputeArrivalButton } from "@/components/DisputeArrivalButton";
import { ShiftTimelineButton } from "@/components/ShiftTimelineButton";
import { KycActionBanner } from "@/components/KycActionBanner";
import { EndShiftEarlyDialog } from "@/components/EndShiftEarlyDialog";
import { MutualFinishDialog } from "@/components/MutualFinishDialog";
import { MutualFinishBanner } from "@/components/MutualFinishBanner";
import { FollowVenueButton } from "@/components/FollowVenueButton";
import { BartenderTierCard } from "@/components/BartenderTierCard";
import { OnboardingProgressStrip } from "@/components/OnboardingProgressStrip";
import { SupportRequestsSection } from "@/components/SupportRequestsSection";

import { PostShiftHighlightDialog } from "@/components/PostShiftHighlightDialog";
import { SaveShiftButton } from "@/components/SaveShiftButton";
import { getMyVenueHistory } from "@/lib/venue-history.functions";

import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { CancelShiftDialog } from "@/components/CancelShiftDialog";
import type { CancelReason } from "@/components/CancelShiftDialog";
import { StrikesPanel } from "@/components/StrikesPanel";
import { ContractorAcknowledgeDialog } from "@/components/ContractorAcknowledgeDialog";
import { CONTRACTOR_TERMS_VERSION } from "@/lib/compliance";
import { toast } from "sonner";
import { downloadIcs } from "@/lib/ics";
import { claimUnlockedMilestones } from "@/lib/milestones.functions";
import { ShiftCompletionEvidence } from "@/components/ShiftCompletionEvidence";
import { ShiftBriefText } from "@/components/ShiftBriefText";
import { useAutoRefresh } from "@/hooks/use-auto-refresh";
import { useRealtimeTables } from "@/hooks/use-realtime-tables";


type ShiftStatus = "open" | "awaiting_payment" | "paid" | "filled" | "completed" | "cancelled" | "disputed";

type Shift = {
  id: string;
  start_time: string;
  end_time: string;
  hourly_rate: number;
  role_type: string;
  requires_dbs: boolean;
  requires_rtw: boolean;
  status: ShiftStatus;
  notes: string | null;
  bartender_id: string | null;
  venue_id: string | null;
  venue_confirmed_at: string | null;
  bartender_confirmed_at: string | null;
  payment_status: string;
  address: string | null;
  postcode: string | null;
  latitude: number | null;
  longitude: number | null;
  favorites_only_until: string | null;
};

function haversineMiles(a: { lat: number; lng: number }, b: { lat: number; lng: number }) {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const R = 3958.8;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(s)));
}

type Profile = {
  id: string;
  full_name: string | null;
  is_verified: boolean;
  has_dbs: boolean;
  cv_file_path: string | null;
  about_me: string | null;
  rtw_status?: string | null;
};

type Review = { id: string; rating: number; comment: string | null; created_at: string };
type Booking = {
  id: string;
  shift_id: string;
  status: "pending" | "accepted" | "denied";
  arrival_venue_confirmed_at: string | null;
  arrival_bartender_confirmed_at: string | null;
  actual_arrival_at?: string | null;
  late_deduction_pence?: number | null;
  late_arrival_logged_at?: string | null;
  arrival_disputed_at?: string | null;
  arrival_dispute_resolution?: string | null;
};
type Payout = { id: string; shift_id: string; amount_pence: number; status: string; created_at: string; kind: "base" | "tip" | null };
type ActiveEngagement = { shift: Shift; booking: Booking };

export const Route = createFileRoute("/_authenticated/bartender/")({
  head: () => ({ meta: [{ title: "Shift Feed — The Hospitality Reserve" }] }),
  component: BartenderPage,
});

function BartenderPage() {
  const [profile, setProfile] = useState<Profile | null>(null);
  const [onboardingStatus, setOnboardingStatus] = useState<string | null>(null);
  const [payoutsEnabled, setPayoutsEnabled] = useState<boolean>(true);
  const [hasStripeAccount, setHasStripeAccount] = useState<boolean>(false);
  

  const [shifts, setShifts] = useState<Shift[]>([]);
  const [favoritedByVenues, setFavoritedByVenues] = useState<Set<string>>(new Set());
  const [reviews, setReviews] = useState<Review[]>([]);
  const [myBookings, setMyBookings] = useState<Booking[]>([]);
  const [activeEngagements, setActiveEngagements] = useState<ActiveEngagement[]>([]);
  const [payouts, setPayouts] = useState<Payout[]>([]);
  const [venueNames, setVenueNames] = useState<Map<string, string>>(new Map());
  const [verifiedVenues, setVerifiedVenues] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [registeringId, setRegisteringId] = useState<string | null>(null);
  const [actingId, setActingId] = useState<string | null>(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelShiftId, setCancelShiftId] = useState<string | null>(null);
  const [aboutDraft, setAboutDraft] = useState("");
  const [savingAbout, setSavingAbout] = useState(false);
  const [uploadingCv, setUploadingCv] = useState(false);
  const [filterPostcode, setFilterPostcode] = useState("");
  const [filterRadius, setFilterRadius] = useState(10);
  const [filterCoords, setFilterCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [applying, setApplying] = useState(false);
  const [recentPostcodes, setRecentPostcodes] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState<"best" | "soonest" | "pay" | "distance">("best");
  const [dbsOnly, setDbsOnly] = useState(false);
  const [followedOnly, setFollowedOnly] = useState(false);
  const [followedVenues, setFollowedVenues] = useState<Set<string>>(new Set());
  const [workedHere, setWorkedHere] = useState<Map<string, number>>(new Map());
  const postcodeInputRef = useRef<HTMLInputElement>(null);

  const RECENT_KEY = "bartender:recentPostcodes";
  const LAST_KEY = "bartender:lastPostcode";
  const LAST_RADIUS_KEY = "bartender:lastRadius";

  const register = useServerFn(registerInterest);
  const confirm = useServerFn(confirmShiftCompletion);
  const cancel = useServerFn(cancelShift);
  const saveDetails = useServerFn(updateProfileDetails);
  const submitCv = useServerFn(submitApplicationCv);
  const lookup = useServerFn(lookupPostcode);

  const persistRecent = (pc: string) => {
    if (typeof window === "undefined") return;
    const norm = pc.trim().toUpperCase();
    if (!norm) return;
    const next = [norm, ...recentPostcodes.filter((p) => p !== norm)].slice(0, 5);
    setRecentPostcodes(next);
    try {
      window.localStorage.setItem(RECENT_KEY, JSON.stringify(next));
      window.localStorage.setItem(LAST_KEY, norm);
    } catch { /* ignore */ }
  };

  const handleApplyFilter = async (postcodeOverride?: string) => {
    const pc = (postcodeOverride ?? filterPostcode).trim();
    if (!pc) { setFilterCoords(null); return; }
    setApplying(true);
    try {
      const coords = await lookup({ data: { postcode: pc } });
      setFilterCoords(coords);
      setFilterPostcode(pc.toUpperCase());
      persistRecent(pc);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not locate postcode.");
      setFilterCoords(null);
    } finally { setApplying(false); }
  };

  const clearFilter = () => { setFilterPostcode(""); setFilterCoords(null); };


  const navigate = useNavigate();
  const load = async (opts?: { silent?: boolean }) => {
    if (!opts?.silent) setLoading(true);
    const { data: { session: __s } } = await supabase.auth.getSession(); const u = { user: __s?.user ?? null };
    if (!u.user) return;
    // Onboarding gate: read status but do NOT redirect. We show an inline banner
    // and hide the shift feed until the bartender is approved.
    const { data: gate } = await supabase
      .from("profiles")
      .select("role,onboarding_status,stripe_payouts_enabled,stripe_account_id")
      .eq("id", u.user.id)
      .maybeSingle();
    const g = gate as { role?: string; onboarding_status?: string; stripe_payouts_enabled?: boolean; stripe_account_id?: string | null } | null;
    setOnboardingStatus(g?.onboarding_status ?? null);
    if (g && g.role === "bartender") {
      if (g.onboarding_status === "rejected") { navigate({ to: "/bartender/rejected", replace: true }); return; }
      setPayoutsEnabled(!!g.stripe_payouts_enabled);
      setHasStripeAccount(!!g.stripe_account_id);
    }



    const [{ data: p }, { data: s }, { data: r }, { data: b }, { data: acceptedData }, { data: po }, { data: favs }] = await Promise.all([
      supabase.from("profiles").select("id,full_name,is_verified,has_dbs,cv_file_path,about_me,stripe_payouts_enabled,role,rtw_status").eq("id", u.user.id).maybeSingle(),
      (supabase.rpc as unknown as (name: string) => Promise<{ data: Shift[] | null; error: unknown }>)("browse_open_shifts")
        .then((res) => ({
          data: ((res.data ?? []) as Shift[]).filter((sh) => new Date(sh.start_time) > new Date()),
          error: res.error,
        })),
      supabase.from("reviews").select("id,rating,comment,created_at").eq("bartender_id", u.user.id).order("created_at", { ascending: false }),
      supabase.from("bookings").select("id,shift_id,status,arrival_venue_confirmed_at,arrival_bartender_confirmed_at").eq("bartender_id", u.user.id),
      supabase
        .from("bookings")
        .select(`
          id,shift_id,status,arrival_venue_confirmed_at,arrival_bartender_confirmed_at,
          actual_arrival_at,late_deduction_pence,late_arrival_logged_at,
          arrival_disputed_at,arrival_dispute_resolution,
          shift:shifts!inner(
            id,start_time,end_time,hourly_rate,role_type,requires_dbs,requires_rtw,status,notes,
            bartender_id,venue_id,venue_confirmed_at,bartender_confirmed_at,payment_status,
            address,postcode,latitude,longitude,favorites_only_until
          )
        `)
        .eq("bartender_id", u.user.id)
        .eq("status", "accepted"),
      supabase.from("payouts").select("id,shift_id,amount_pence,status,created_at,kind").eq("bartender_id", u.user.id).order("created_at", { ascending: false }),
      supabase.from("favorite_bartenders").select("venue_id").eq("bartender_id", u.user.id),
      supabase.from("identity_verifications").select("status,created_at,updated_at").eq("user_id", u.user.id).order("updated_at", { ascending: false }).limit(1).maybeSingle(),
    ]);
    const acceptedRecords = (acceptedData ?? []) as unknown as Array<{
      id: string;
      shift_id: string;
      status: "pending" | "accepted" | "denied";
      arrival_venue_confirmed_at: string | null;
      arrival_bartender_confirmed_at: string | null;
      shift: Shift | null;
    }>;
    const activeRows = acceptedRecords
      .filter((row) => row.shift && !["completed", "cancelled"].includes(row.shift.status))
      .map((row) => ({
        booking: {
          id: row.id,
          shift_id: row.shift_id,
          status: row.status,
          arrival_venue_confirmed_at: row.arrival_venue_confirmed_at,
          arrival_bartender_confirmed_at: row.arrival_bartender_confirmed_at,
        },
        shift: row.shift as Shift,
      }))
      .sort((a, b) => new Date(a.shift.start_time).getTime() - new Date(b.shift.start_time).getTime());
    setProfile(p as Profile);
    
    setAboutDraft((p as Profile | null)?.about_me ?? "");
    setShifts((s ?? []) as Shift[]);
    setReviews((r ?? []) as Review[]);
    setMyBookings((b ?? []) as Booking[]);
    setActiveEngagements(activeRows);
    setPayouts((po ?? []) as Payout[]);
    setFavoritedByVenues(new Set((favs ?? []).map((f) => f.venue_id as string)));

    try {
      const { data: fv } = await (supabase.from as unknown as (t: string) => { select: (c: string) => Promise<{ data: { venue_id: string }[] | null }> })("bartender_followed_venues").select("venue_id");
      setFollowedVenues(new Set((fv ?? []).map((r) => r.venue_id)));
    } catch { /* table may not yet exist for some users */ }

    // Pull how many times this bartender has worked at each venue so the
    // ShiftCard can render a "Worked here X×" reapply badge.
    try {
      const hist = await getMyVenueHistory({ data: undefined });
      const map = new Map<string, number>();
      (hist.rows ?? []).forEach((r) => map.set(r.venue_id, r.completed_count));
      setWorkedHere(map);
    } catch { /* non-fatal */ }

    // Fetch venue names for accepted engagements even after they have started.
    const upcomingShiftIds = new Set(activeRows.map(({ shift }) => shift.id));
    const upcomingVenueIds = Array.from(
      new Set(
        [...((s ?? []) as Shift[]), ...activeRows.map(({ shift }) => shift)]
          .filter((sh) => upcomingShiftIds.has(sh.id) || (sh.bartender_id && sh.status !== "open"))
          .map((sh) => sh.venue_id)
          .filter((id): id is string => !!id),
      ),
    );
    if (upcomingVenueIds.length) {
      const { data: venues } = await supabase.from("profiles").select("id,full_name,is_verified_business").in("id", upcomingVenueIds);
      const names = new Map<string, string>();
      const verified = new Set<string>();
      (venues ?? []).forEach((v) => {
        names.set(v.id as string, (v.full_name as string) || "Venue");
        if ((v as { is_verified_business?: boolean }).is_verified_business) verified.add(v.id as string);
      });
      setVenueNames(names);
      setVerifiedVenues(verified);
    } else {
      setVenueNames(new Map());
      setVerifiedVenues(new Set());
    }

    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  // Realtime is the primary live channel; polling stays as a slow safety net
  // in case a websocket drops.
  useAutoRefresh(() => load({ silent: true }), { intervalMs: 60000 });

  // ---- Live updates (Supabase Realtime) -------------------------------------
  const reloadTimer = useRef<number | null>(null);
  const scheduleReload = () => {
    if (typeof window === "undefined") return;
    if (reloadTimer.current) window.clearTimeout(reloadTimer.current);
    reloadTimer.current = window.setTimeout(() => { void load({ silent: true }); }, 400);
  };
  const knownShiftIds = useRef<Set<string>>(new Set());
  useEffect(() => { knownShiftIds.current = new Set(shifts.map((s) => s.id)); }, [shifts]);
  const myShiftStatus = useRef<Map<string, string>>(new Map());
  useEffect(() => {
    myShiftStatus.current = new Map(activeEngagements.map((e) => [e.shift.id, e.shift.status]));
  }, [activeEngagements]);

  const uid = profile?.id ?? null;
  useRealtimeTables(
    "bartender-dashboard",
    uid ? [{ table: "shifts" }, { table: "bookings", filter: `bartender_id=eq.${uid}` }] : [],
    (ev) => {
      const row = (ev.new ?? ev.old) as { id?: string; status?: string } | null;
      if (ev.table === "shifts" && row?.id) {
        if (ev.eventType === "INSERT" && row.status === "open" && !knownShiftIds.current.has(row.id)) {
          toast("A new shift has just been posted", { description: "It's already in your feed below." });
        } else if (ev.eventType === "UPDATE" && myShiftStatus.current.has(row.id)) {
          const prev = myShiftStatus.current.get(row.id);
          if (row.status && prev && prev !== row.status) {
            toast(`Your engagement is now ${String(row.status).replace(/_/g, " ")}`);
          }
        }
      }
      scheduleReload();
    },
    !!uid,
  );


  // Premium micro-delight: detect newly unlocked milestones and celebrate once.
  const claimMilestones = useServerFn(claimUnlockedMilestones);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const unlocked = await claimMilestones({ data: undefined });
        if (cancelled || !unlocked.length) return;
        const { celebrate } = await import("@/lib/celebrate");
        celebrate("big");
        for (const m of unlocked) toast.success(`🎉 ${m.label}`);
      } catch {
        /* non-fatal */
      }
    })();
    return () => { cancelled = true; };
  }, [claimMilestones]);

  // Tick every 30s so shifts that just started disappear from the feed without a refresh.
  const [nowTick, setNowTick] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNowTick(Date.now()), 30_000);
    return () => clearInterval(id);
  }, []);


  // Hydrate saved postcode / radius / recents from localStorage and auto-apply.
  const [hydratedFilter, setHydratedFilter] = useState(false);
  useEffect(() => {
    if (hydratedFilter || typeof window === "undefined") return;
    setHydratedFilter(true);
    try {
      const recRaw = window.localStorage.getItem(RECENT_KEY);
      if (recRaw) {
        const parsed = JSON.parse(recRaw);
        if (Array.isArray(parsed)) setRecentPostcodes(parsed.filter((x) => typeof x === "string").slice(0, 5));
      }
      const lastR = window.localStorage.getItem(LAST_RADIUS_KEY);
      if (lastR) {
        const n = Number(lastR);
        if (Number.isFinite(n) && n >= 1 && n <= 50) setFilterRadius(n);
      }
      const last = window.localStorage.getItem(LAST_KEY);
      if (last) {
        setFilterPostcode(last);
        void handleApplyFilter(last);
      }
    } catch { /* ignore */ }
  }, [hydratedFilter]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try { window.localStorage.setItem(LAST_RADIUS_KEY, String(filterRadius)); } catch { /* ignore */ }
  }, [filterRadius]);



  const [pendingShiftId, setPendingShiftId] = useState<string | null>(null);

  const handleRegister = (id: string) => {
    const s = (profile?.rtw_status ?? "not_submitted") as string;
    if (s === "rejected" || s === "expired") {
      toast.error(
        s === "expired"
          ? "Right to Work expired — re-verify via Veriff before accepting shifts."
          : "Right to Work needs attention — resubmit via Veriff before accepting shifts.",
      );
      if (typeof document !== "undefined") {
        const el = document.getElementById("rtw-check-card");
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
          el.classList.add("ring-2", "ring-destructive");
          setTimeout(() => el.classList.remove("ring-2", "ring-destructive"), 2400);
        }
      }
      return;
    }
    if (!payoutsEnabled) {
      toast.info("Connect your bank account first so we can pay you for this shift.");
      navigate({ to: "/bartender/payouts-setup" });
      return;
    }
    setPendingShiftId(id);
  };

  const confirmRegister = async () => {
    const id = pendingShiftId;
    if (!id) return;
    setRegisteringId(id);
    try {
      await register({
        data: {
          shift_id: id,
          contractor_terms_accepted: true as const,
          contractor_terms_version: CONTRACTOR_TERMS_VERSION,
          insurance_confirmed: true as const,
        },
      });
      toast.success("Interest registered.");
      setPendingShiftId(null);
      await load();
    } catch (err) {
      // Conflict guard: another bartender may have taken the shift while this
      // one was on the accept flow. Re-check the live status before blaming
      // the user, and refresh that shift in the feed either way.
      const msg = err instanceof Error ? err.message : "Could not register interest.";
      let stillOpen = true;
      try {
        const { data: fresh } = await supabase.from("shifts").select("id,status").eq("id", id).maybeSingle();
        stillOpen = !!fresh && (fresh as { status?: string }).status === "open";
        if (!stillOpen) {
          setShifts((prev) =>
            prev
              .map((s) => (s.id === id ? { ...s, status: (((fresh as { status?: string } | null)?.status ?? "filled") as Shift["status"]) } : s))
              .filter((s) => s.id !== id),
          );
        }
      } catch { /* fall back to the server message */ }
      toast.error(stillOpen ? msg : "This shift was just filled by someone else — it's been removed from your feed.");
      setPendingShiftId(null);
      void load({ silent: true });
    } finally {
      setRegisteringId(null);
    }

  };

  const handleConfirm = async (id: string) => {
    setActingId(id);
    try {
      const res = await confirm({ data: { shift_id: id } });
      toast.success(res.completed ? "Confirmed — engagement closed and payout queued." : "Confirmed. Awaiting the venue to confirm.");
      await load();
    } catch (err) { toast.error(err instanceof Error ? err.message : "Could not confirm."); }
    finally { setActingId(null); }
  };

  const handleCancel = (id: string) => {
    setCancelShiftId(id);
    setCancelDialogOpen(true);
  };
  const doCancel = async (reason: CancelReason, reason_text: string) => {
    if (!cancelShiftId) return;
    setCancelDialogOpen(false);
    setActingId(cancelShiftId);
    setFlowSubject({ shiftId: cancelShiftId });
    flowBreadcrumb("cancellation", "cancel requested", { shift_id: cancelShiftId, role: "bartender", reason });
    try {
      const res = await cancel({ data: { shift_id: cancelShiftId, environment: getStripeEnvironment(), reason, reason_text: reason_text || undefined } });
      if ("error" in res) throw new Error(res.error);
      if (res.suspended) {
        toast.error("Strike 3 — your account has been suspended. Submit an appeal below.");
      } else if (res.strike_issued && res.strike_number === 2) {
        toast.warning("Strike 2 — FINAL WARNING. Next late cancellation will suspend your account.");
      } else if (res.strike_issued) {
        toast.warning("Strike 1 — late cancellation logged. Further late cancellations can lead to suspension.");
      } else {
        toast.success("Engagement cancelled.");
      }
      await load();
    } catch (err) {
      const t = reportCancellationFailure(err, { shift_id: cancelShiftId, role: "bartender", reason });
      toast.error(t.title, { description: t.description, duration: 12000 });
    }
    finally { setActingId(null); setCancelShiftId(null); }
  };


  const handleSaveAbout = async () => {
    setSavingAbout(true);
    try { await saveDetails({ data: { about_me: aboutDraft } }); toast.success("About me updated."); await load(); }
    catch (err) { toast.error(err instanceof Error ? err.message : "Could not save."); }
    finally { setSavingAbout(false); }
  };

  const handleCvUpload = async (file: File) => {
    const allowed = ["application/pdf", "application/msword", "application/vnd.openxmlformats-officedocument.wordprocessingml.document"];
    if (!allowed.includes(file.type)) { toast.error("Please upload a PDF or Word document."); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error("CV must be under 10MB."); return; }
    setUploadingCv(true);
    try {
      const { data: { session: __s } } = await supabase.auth.getSession(); const u = { user: __s?.user ?? null };
      if (!u.user) throw new Error("Not signed in.");
      const ext = file.name.split(".").pop() || "pdf";
      const path = `${u.user.id}/cv.${ext}`;
      const { error: upErr } = await supabase.storage.from("cvs").upload(path, file, { upsert: true, contentType: file.type });
      if (upErr) throw upErr;
      // If awaiting CV review, go through the admin-notifying flow.
      if (onboardingStatus === "pending_cv_review") {
        const r = await submitCv({ data: { cv_file_path: path } });
        if ("error" in r) throw new Error(r.error);
        toast.success("CV submitted for review. Admin has been notified.");
      } else {
        await saveDetails({ data: { cv_file_path: path } });
        toast.success("CV uploaded.");
      }
      await load();
    } catch (err) { toast.error(err instanceof Error ? err.message : "Could not upload CV."); }
    finally { setUploadingCv(false); }
  };


  const handleViewCv = async () => {
    if (!profile?.cv_file_path) return;
    const { data, error } = await supabase.storage.from("cvs").createSignedUrl(profile.cv_file_path, 60);
    if (error || !data) { toast.error("Could not open CV."); return; }
    window.open(data.signedUrl, "_blank", "noopener,noreferrer");
  };

  const myUpcoming = activeEngagements;
  const rtwVerified = (profile?.rtw_status ?? null) === "verified";
  const rtwStatusRaw = (profile?.rtw_status ?? "not_submitted") as string;
  const rtwBlocked = rtwStatusRaw === "rejected" || rtwStatusRaw === "expired";
  const rtwBlockedLabel = rtwStatusRaw === "expired" ? "Expired" : "Action required";
  const rtwBlockedReason = rtwStatusRaw === "expired"
    ? "Your Right to Work has expired. Re-verify via Veriff to accept paid engagements."
    : "Your Right to Work needs attention. Resubmit via Veriff to accept paid engagements.";
  const hasDbs = !!profile?.has_dbs;
  const openShifts = shifts.filter((s) => {
    if (s.status !== "open") return false;
    // Hide shifts whose start time has passed — re-evaluated each tick.
    if (new Date(s.start_time).getTime() <= nowTick) return false;
    // Hide RTW-required shifts from bartenders who aren't verified —
    // they legally can't accept them.
    if (s.requires_rtw && !rtwVerified) return false;
    // Hide DBS-required shifts from bartenders without DBS on file.
    if (s.requires_dbs && !hasDbs) return false;
    if (s.favorites_only_until && new Date(s.favorites_only_until).getTime() > nowTick) {
      return !!(s.venue_id && favoritedByVenues.has(s.venue_id));
    }
    return true;
  });
  const hiddenRtwCount = shifts.filter(
    (s) => s.status === "open" && s.requires_rtw && !rtwVerified,
  ).length;
  const hiddenDbsCount = shifts.filter(
    (s) => s.status === "open" && s.requires_dbs && !hasDbs,
  ).length;
  const openWithDistance = openShifts.map((s) => {
    const distance = filterCoords && s.latitude != null && s.longitude != null
      ? haversineMiles(filterCoords, { lat: s.latitude, lng: s.longitude })
      : null;
    return { shift: s, distance };
  });
  const filteredOpen = (filterCoords
    ? openWithDistance.filter(({ distance }) => distance != null && distance <= filterRadius)
    : openWithDistance
  ).filter(({ shift }) => {
    if (dbsOnly && !shift.requires_dbs) return false;
    if (followedOnly && (!shift.venue_id || !followedVenues.has(shift.venue_id))) return false;
    return true;
  });
  // "Best match" composite score: closer + higher pay + sooner = better.
  // Each axis is normalised to 0–1 across the visible set, then summed with weights.
  const scoreMap = (() => {
    const rates = filteredOpen.map(({ shift }) => getPayRate(shift.role_type).bartender_pence);
    const maxRate = Math.max(1, ...rates);
    const starts = filteredOpen.map(({ shift }) => new Date(shift.start_time).getTime() - nowTick);
    const maxStart = Math.max(1, ...starts);
    const dists = filteredOpen.map(({ distance }) => distance ?? 50);
    const maxDist = Math.max(1, ...dists);
    const m = new Map<string, number>();
    for (const { shift, distance } of filteredOpen) {
      const payNorm = getPayRate(shift.role_type).bartender_pence / maxRate;
      const soonNorm = 1 - (new Date(shift.start_time).getTime() - nowTick) / maxStart;
      const distNorm = 1 - (distance ?? 50) / maxDist;
      // Pay 45%, proximity 35%, recency 20%.
      m.set(shift.id, payNorm * 0.45 + distNorm * 0.35 + soonNorm * 0.2);
    }
    return m;
  })();
  const sorted = [...filteredOpen].sort((a, b) => {
    if (sortBy === "pay") {
      return getPayRate(b.shift.role_type).bartender_pence - getPayRate(a.shift.role_type).bartender_pence;
    }
    if (sortBy === "distance") {
      const ad = a.distance ?? Infinity;
      const bd = b.distance ?? Infinity;
      if (ad !== bd) return ad - bd;
      return new Date(a.shift.start_time).getTime() - new Date(b.shift.start_time).getTime();
    }
    if (sortBy === "soonest") {
      return new Date(a.shift.start_time).getTime() - new Date(b.shift.start_time).getTime();
    }
    // best match
    return (scoreMap.get(b.shift.id) ?? 0) - (scoreMap.get(a.shift.id) ?? 0);
  });
  const bestMatchIds = new Set(
    [...scoreMap.entries()].sort((a, b) => b[1] - a[1]).slice(0, 3).map(([id]) => id),
  );
  const open = sorted;
  const avg = reviews.length ? (reviews.reduce((a, r) => a + r.rating, 0) / reviews.length).toFixed(1) : null;
  // Completed engagements (for streak/milestone badge) derived from paid-out base payouts.
  const completedCount = new Set(
    payouts.filter((p) => p.status === "paid_out" && p.kind !== "tip").map((p) => p.shift_id),
  ).size;
  const milestoneLabel =
    completedCount >= 50 ? "Veteran · 50+ engagements"
    : completedCount >= 25 ? "Seasoned · 25+ engagements"
    : completedCount >= 10 ? "Regular · 10+ engagements"
    : completedCount >= 5 ? "Rising · 5+ engagements"
    : completedCount >= 1 ? `${completedCount} engagement${completedCount === 1 ? "" : "s"} completed`
    : null;

  const isTip = (p: Payout) => p.kind === "tip";
  const basePayable = payouts.filter((p) => p.status === "payable" && !isTip(p)).reduce((a, p) => a + p.amount_pence, 0);
  const basePaidOut = payouts.filter((p) => p.status === "paid_out" && !isTip(p)).reduce((a, p) => a + p.amount_pence, 0);
  const tipPayable = payouts.filter((p) => p.status === "payable" && isTip(p)).reduce((a, p) => a + p.amount_pence, 0);
  const tipPaidOut = payouts.filter((p) => p.status === "paid_out" && isTip(p)).reduce((a, p) => a + p.amount_pence, 0);
  const payable = basePayable + tipPayable;
  const paidOut = basePaidOut + tipPaidOut;

  return (
    <div className="min-h-screen bg-background">
      <ReserveHeader role="bartender" payoutsEnabled={payoutsEnabled} />
      {onboardingStatus === "approved" && (
        <KycActionBanner hasAccount={hasStripeAccount} enabled={payoutsEnabled} />
      )}
      <div className="mx-auto max-w-7xl px-6 pt-6">
        <OnboardingProgressStrip />
        <div className="mt-4">
          <SupportRequestsSection />
        </div>
      </div>
      <main className="mx-auto max-w-7xl px-6 py-10 grid gap-10 lg:grid-cols-[320px_1fr]">



        <aside>
          <p className="tracking-wide-luxury text-accent">Member</p>
          <h1 className="mt-3 font-display text-3xl">{profile?.full_name || "Welcome"}</h1>
          <span className="gold-divider mt-4" />

          <div className="mt-6 space-y-3">
            {profile?.is_verified && <FoundingMemberBadge />}
            {milestoneLabel && (
              <div className="border border-accent/40 bg-accent/5 px-3 py-2 text-[0.65rem] tracking-wide-luxury text-accent uppercase">
                ★ {milestoneLabel}
              </div>
            )}
            <div className="text-xs tracking-wide-luxury text-muted-foreground space-y-1.5 pt-2">
              <p>· {profile?.is_verified ? "Identity Verified" : "Identity check pending (handled with your Right to Work check)"}</p>
              <p>· {profile?.has_dbs ? "DBS on File" : "DBS Not on File"}</p>
              {avg && <p>· {avg} ★ across {reviews.length} engagement{reviews.length === 1 ? "" : "s"}</p>}
            </div>
          </div>

          <div className="mt-6"><BartenderTierCard /></div>
          <div className="mt-3 grid grid-cols-2 gap-2">
            <Link to="/bartender/matches" className="rounded-md border border-accent/60 bg-accent/5 px-3 py-3 text-center text-xs tracking-wide-luxury text-accent hover:bg-accent/10 transition">
              ✨ AI matches
            </Link>
            <Link to="/bartender/radar" className="rounded-md border border-accent/60 bg-accent/5 px-3 py-3 text-center text-xs tracking-wide-luxury text-accent hover:bg-accent/10 transition">
              📍 Live radar
            </Link>
            <Link to="/bartender/watchlist" className="rounded-md border border-border bg-card px-3 py-3 text-center text-xs tracking-wide-luxury text-muted-foreground hover:text-accent hover:border-accent/40 transition">
              🔔 Watchlist
            </Link>
            <Link to="/bartender/previous" className="rounded-md border border-border bg-card px-3 py-3 text-center text-xs tracking-wide-luxury text-muted-foreground hover:text-accent hover:border-accent/40 transition">
              🗂 Previous
            </Link>
            <Link to="/bartender/earnings" className="col-span-2 block w-full rounded-md border border-border bg-card px-4 py-3 text-center text-xs tracking-wide-luxury text-muted-foreground hover:text-accent hover:border-accent/40 transition">
              View earnings →
            </Link>
          </div>

          <VeriffRtwStatusPanel />


          <div id="rtw-check-card" className="scroll-mt-24 transition-shadow">
            <RtwCheckCard />
          </div>


          <div className="mt-8 border border-border bg-card p-5">
            <p className="font-display text-lg">Earnings</p>
            <span className="gold-divider mt-3" />
            <div className="mt-4 text-sm text-foreground/80 space-y-1">
              <p>Payable: <span className="font-display text-accent">£{(payable / 100).toFixed(2)}</span></p>
              <p className="pl-3 text-xs text-muted-foreground">· Base wage: £{(basePayable / 100).toFixed(2)}</p>
              <p className="pl-3 text-xs text-muted-foreground">· Tips (100% to you): £{(tipPayable / 100).toFixed(2)}</p>
              <p className="pt-1">Paid out: <span className="text-muted-foreground">£{(paidOut / 100).toFixed(2)}</span></p>
              <p className="pl-3 text-xs text-muted-foreground">· Base: £{(basePaidOut / 100).toFixed(2)} · Tips: £{(tipPaidOut / 100).toFixed(2)}</p>
            </div>
            <p className="mt-3 text-[0.65rem] text-muted-foreground">Tips pass 100% to you under the UK Tipping Act 2023.</p>
            <Link
              to="/bartender/payouts-setup"
              className="mt-4 inline-block w-full text-center border border-border px-4 py-3 text-xs tracking-wide-luxury text-muted-foreground hover:border-foreground hover:text-foreground transition-colors"
            >
              Manage payout account →
            </Link>
          </div>

          <div className="mt-6 space-y-2">
            <Link
              to="/portfolio"
              className="inline-block w-full text-center border border-accent px-4 py-3 text-xs tracking-wide-luxury text-accent hover:bg-accent hover:text-accent-foreground transition-colors"
            >
              Manage Portfolio →
            </Link>
            <Link
              to="/history"
              className="inline-block w-full text-center border border-border px-4 py-3 text-xs tracking-wide-luxury text-muted-foreground hover:border-foreground hover:text-foreground transition-colors"
            >
              View Engagement History →
            </Link>
          </div>



          <div className="mt-8 border border-border bg-card p-5">
            <p className="font-display text-lg">Your CV</p>
            <p className="mt-2 text-xs text-muted-foreground leading-relaxed">
              Upload a PDF or Word document.
            </p>
            {profile?.cv_file_path && (
              <button type="button" onClick={handleViewCv} className="mt-4 text-xs tracking-wide-luxury text-accent underline underline-offset-4">
                View current CV
              </button>
            )}
            <label className="mt-3 block">
              <input
                type="file"
                accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                disabled={uploadingCv}
                onChange={(e) => { const f = e.target.files?.[0]; if (f) handleCvUpload(f); e.target.value = ""; }}
                className="block w-full text-xs text-muted-foreground file:mr-3 file:border file:border-border file:bg-background file:px-3 file:py-2 file:text-foreground file:tracking-wide-luxury file:cursor-pointer hover:file:bg-muted"
              />
            </label>
            {uploadingCv && <p className="mt-2 text-xs text-muted-foreground">Uploading…</p>}
          </div>

          <div className="mt-8 border border-border bg-card p-5">
            <p className="font-display text-lg">About Me</p>
            <Textarea rows={6} value={aboutDraft} onChange={(e) => setAboutDraft(e.target.value)} placeholder="Tell venues about yourself…" className="mt-4" maxLength={2000} />
            <Button onClick={handleSaveAbout} disabled={savingAbout} className="mt-3 w-full rounded-none bg-foreground text-background hover:bg-foreground/90 tracking-wide-luxury">
              {savingAbout ? "Saving…" : "Save About Me"}
            </Button>
          </div>

          {reviews.length > 0 && (
            <div className="mt-8">
              <p className="tracking-wide-luxury text-accent">Record</p>
              <h3 className="mt-3 font-display text-xl">Reviews</h3>
              <span className="gold-divider mt-3" />
              <div className="mt-4 space-y-3">
                {reviews.map((r) => (
                  <div key={r.id} className="border border-border bg-card p-4">
                    <p className="text-accent text-xs tracking-wide-luxury">{"★".repeat(r.rating)}{"·".repeat(5 - r.rating)}</p>
                    {r.comment && <p className="mt-2 text-sm text-foreground/80">{r.comment}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="mt-6"><StrikesPanel /></div>
        </aside>

        <section>
          {onboardingStatus && onboardingStatus !== "approved" && (
            <div className="mb-10 border border-accent/40 bg-accent/5 p-6">
              <p className="tracking-wide-luxury text-accent">Action Required</p>
              <h2 className="mt-3 font-display text-2xl">Complete your application</h2>
              <span className="gold-divider mt-3" />
              <p className="mt-4 text-sm text-foreground/80 leading-relaxed">
                Before any shifts will appear here, upload your CV. Our automated check confirms your bar
                experience — a minimum of 6 months combined is required.
              </p>
              <ol className="mt-5 space-y-3 text-sm text-foreground/85">
                <li className="flex items-start gap-3">
                  <span className="mt-0.5 text-xs tracking-wide-luxury text-accent">01</span>
                  <div className="flex-1">
                    <p className="font-medium">Upload your CV</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      PDF, DOCX or TXT. Checked automatically in under a minute.
                    </p>
                    {profile?.cv_file_path ? (
                      <p className="mt-2 text-xs text-accent">✓ CV received.</p>
                    ) : null}
                    <Link to="/bartender/application" className="mt-2 inline-block border border-accent px-4 py-2 text-xs tracking-wide-luxury text-accent hover:bg-accent hover:text-accent-foreground transition-colors">
                      Upload Your Experience →
                    </Link>
                  </div>
                </li>
              </ol>
            </div>
          )}

          {onboardingStatus === "approved" && !payoutsEnabled && (
            <div className="mb-10 border border-accent/40 bg-accent/5 p-6">
              <p className="tracking-wide-luxury text-accent">Bank account required</p>
              <h2 className="mt-3 font-display text-2xl">Connect payouts to apply for shifts</h2>
              <span className="gold-divider mt-3" />
              <p className="mt-4 text-sm text-foreground/80 leading-relaxed">
                Browse the live register below. When you're ready to register interest in a shift, we'll guide you through a 2-minute Stripe bank setup so you can be paid.
              </p>
              <Link
                to="/bartender/payouts-setup"
                className="mt-5 inline-block border border-accent px-4 py-2 text-xs tracking-wide-luxury text-accent hover:bg-accent hover:text-accent-foreground transition-colors"
              >
                Connect bank account →
              </Link>
            </div>
          )}

          {myUpcoming.length > 0 && (
            <div className="mb-12">
              <p className="tracking-wide-luxury text-accent">Live</p>
              <h2 className="mt-3 font-display text-3xl">Active Engagements</h2>
              <span className="gold-divider mt-4" />
              <div className="mt-6 space-y-4">
                {myUpcoming.map(({ shift: s, booking }) => {
                  return (
                  <ShiftCard
                    key={s.id}
                    shift={s}
                    venueName={s.venue_id ? venueNames.get(s.venue_id) ?? null : null}
                    venueVerified={s.venue_id ? verifiedVenues.has(s.venue_id) : false}
                    workedHereCount={s.venue_id ? workedHere.get(s.venue_id) ?? 0 : 0}
                    onConfirm={() => handleConfirm(s.id)}
                    onCancel={() => handleCancel(s.id)}
                    acting={actingId === s.id}
                    bartenderConfirmed={!!s.bartender_confirmed_at}
                    booking={booking}
                    onArrivalConfirmed={load}
                  />
                  );
                })}

              </div>
            </div>
          )}

          {onboardingStatus === "approved" && (<>
            <p className="tracking-wide-luxury text-accent">The Register</p>
            <h2 className="mt-3 font-display text-3xl">Available Engagements</h2>
            <span className="gold-divider mt-4" />
          </>)}

          {onboardingStatus === "approved" && (<>
          <div className="mt-6 border border-border bg-card p-5 flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[160px]">
              <label className="block text-[0.65rem] tracking-wide-luxury text-muted-foreground mb-1">Your Postcode</label>
              <input
                ref={postcodeInputRef}
                value={filterPostcode}
                onChange={(e) => setFilterPostcode(e.target.value.toUpperCase())}
                placeholder="SW1A 1AA"
                className="w-full border border-border bg-background px-3 py-2 text-sm"
              />
            </div>
            <div className="min-w-[140px]">
              <label className="block text-[0.65rem] tracking-wide-luxury text-muted-foreground mb-1">Within {filterRadius} mi</label>
              <input
                type="range" min={1} max={50} step={1}
                value={filterRadius}
                onChange={(e) => setFilterRadius(Number(e.target.value))}
                className="w-full"
              />
              <div className="mt-2 flex gap-2">
                {[
                  { label: "2km", mi: 1 },
                  { label: "5km", mi: 3 },
                  { label: "10km", mi: 6 },
                ].map((p) => (
                  <button
                    key={p.label}
                    type="button"
                    onClick={() => setFilterRadius(p.mi)}
                    className={`text-[0.6rem] tracking-wide-luxury border px-2 py-1 transition-colors ${
                      filterRadius === p.mi
                        ? "border-accent text-accent"
                        : "border-border text-muted-foreground hover:border-foreground/50"
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
            <Button onClick={() => handleApplyFilter()} disabled={applying} className="rounded-none bg-foreground text-background hover:bg-foreground/90 tracking-wide-luxury">
              {applying ? "Locating…" : "Apply"}
            </Button>
            {filterCoords && (
              <Button onClick={clearFilter} variant="outline" className="rounded-none border-border tracking-wide-luxury text-xs">Clear</Button>
            )}
          </div>

          {recentPostcodes.length > 0 && (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <span className="text-[0.6rem] tracking-wide-luxury text-muted-foreground">Recent:</span>
              {recentPostcodes.map((pc) => (
                <button
                  key={pc}
                  type="button"
                  onClick={() => handleApplyFilter(pc)}
                  className="text-[0.65rem] tracking-wide-luxury border border-border px-2 py-1 hover:border-accent hover:text-accent transition-colors"
                >
                  {pc}
                </button>
              ))}
            </div>
          )}

          <div className="mt-6 space-y-4">
            {!loading && hiddenRtwCount > 0 && (() => {
              const s = profile?.rtw_status ?? "not_submitted";
              const label =
                s === "submitted" ? "In review"
                : s === "rejected" ? "Action required"
                : s === "expired" ? "Expired"
                : "Not started";
              const help =
                s === "submitted" ? "We're reviewing your Veriff check — RTW-required engagements unlock automatically once verified."
                : s === "rejected" ? "Your last submission was not accepted. Resubmit in the Right to Work card to unlock these engagements."
                : s === "expired" ? "Your Right to Work has expired. Re-verify in the Right to Work card to unlock these engagements."
                : "Complete your Veriff-backed Right to Work check in the card on your profile to unlock these engagements.";
              const tone = s === "submitted"
                ? "border-amber-400/40 bg-amber-400/5"
                : (s === "rejected" || s === "expired")
                  ? "border-destructive/40 bg-destructive/5"
                  : "border-border bg-card";
              return (
                <div className={`border p-4 text-xs text-foreground/80 ${tone}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="border px-2 py-0.5 text-[10px] tracking-wide-luxury uppercase">
                      Right to Work · {label}
                    </span>
                    <span>{hiddenRtwCount} engagement{hiddenRtwCount === 1 ? "" : "s"} hidden</span>
                  </div>
                  <p className="mt-2">{help}</p>
                </div>
              );
            })()}
            {!loading && hiddenDbsCount > 0 && (
              <div className="border border-amber-400/40 bg-amber-400/5 p-4 text-xs text-foreground/80">
                {hiddenDbsCount} engagement{hiddenDbsCount === 1 ? "" : "s"} hidden — these require an Enhanced DBS on file.
                Add your DBS certificate to your profile to unlock them.
              </div>
            )}
            {loading && <p className="text-sm text-muted-foreground">Loading…</p>}
            {!loading && open.length > 0 && (
              <div className="flex items-center gap-2 flex-wrap pb-1">
                <span className="text-[0.6rem] tracking-wide-luxury text-muted-foreground uppercase">Sort by</span>
                {([
                  { id: "best", label: "Best match" },
                  { id: "soonest", label: "Soonest" },
                  { id: "pay", label: "Highest pay" },
                  { id: "distance", label: "Closest" },
                ] as const).map((o) => (
                  <button
                    key={o.id}
                    type="button"
                    onClick={() => setSortBy(o.id)}
                    className={`text-[0.65rem] tracking-wide-luxury border px-2 py-1 transition-colors ${
                      sortBy === o.id
                        ? "border-accent text-accent"
                        : "border-border text-muted-foreground hover:border-foreground/50"
                    }`}
                  >
                    {o.label}
                  </button>
                ))}
                <span className="mx-2 hidden sm:inline text-[0.6rem] text-muted-foreground">·</span>
                <button
                  type="button"
                  onClick={() => setDbsOnly((v) => !v)}
                  aria-pressed={dbsOnly}
                  className={`text-[0.65rem] tracking-wide-luxury border px-2 py-1 transition-colors ${
                    dbsOnly ? "border-accent text-accent" : "border-border text-muted-foreground hover:border-foreground/50"
                  }`}
                  title="Show only shifts that require DBS"
                >
                  DBS required
                </button>
                <button
                  type="button"
                  onClick={() => setFollowedOnly((v) => !v)}
                  aria-pressed={followedOnly}
                  disabled={followedVenues.size === 0}
                  className={`text-[0.65rem] tracking-wide-luxury border px-2 py-1 transition-colors ${
                    followedOnly ? "border-accent text-accent" : "border-border text-muted-foreground hover:border-foreground/50"
                  } ${followedVenues.size === 0 ? "opacity-50 cursor-not-allowed" : ""}`}
                  title={followedVenues.size === 0 ? "Follow a venue to enable this filter" : "Only show shifts from venues you follow"}
                >
                  Followed venues{followedVenues.size > 0 ? ` (${followedVenues.size})` : ""}
                </button>
              </div>
            )}
            {!loading && open.length === 0 && (
              <div className="border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
                {filterCoords ? `No engagements within ${filterRadius} miles.` : "No current opportunities available."}
              </div>
            )}
            {open.map(({ shift: s, distance }) => {
              const booking = myBookings.find((b) => b.shift_id === s.id);
              const earlyAccess = !!(
                s.favorites_only_until &&
                new Date(s.favorites_only_until).getTime() > Date.now()
              );
              return (
                <ShiftCard
                  key={s.id}
                  shift={s}
                  distanceMiles={distance}
                  bookingStatus={booking?.status ?? null}
                  onRegister={() => handleRegister(s.id)}
                  registering={registeringId === s.id}
                  earlyAccess={earlyAccess}
                  bestMatch={bestMatchIds.has(s.id)}
                  venueName={s.venue_id ? venueNames.get(s.venue_id) ?? null : null}
                  venueVerified={s.venue_id ? verifiedVenues.has(s.venue_id) : false}
                  workedHereCount={s.venue_id ? workedHere.get(s.venue_id) ?? 0 : 0}
                  rtwBlocked={rtwBlocked}
                  rtwBlockedLabel={rtwBlockedLabel}
                  rtwBlockedReason={rtwBlockedReason}
                />
              );
            })}
          </div>
          </>)}
        </section>
      </main>

      <PostShiftHighlightDialog />

      <CancelShiftDialog
        open={cancelDialogOpen}
        onOpenChange={setCancelDialogOpen}
        onConfirm={doCancel}
        busy={!!actingId}
        policyText="You'll be removed from this engagement and refunded if already paid."
      />

      <ContractorAcknowledgeDialog
        open={!!pendingShiftId}
        onOpenChange={(v) => { if (!v) setPendingShiftId(null); }}
        onConfirm={confirmRegister}
        busy={!!registeringId}
      />
    </div>
  );
}

function ShiftCard({
  shift, onRegister, registering, bookingStatus,
  onConfirm, onCancel, acting, bartenderConfirmed, distanceMiles, earlyAccess, bestMatch,
  booking, onArrivalConfirmed, venueName, venueVerified,
  rtwBlocked, rtwBlockedLabel, rtwBlockedReason, workedHereCount,
}: {
  shift: Shift;
  onRegister?: () => void;
  registering?: boolean;
  bookingStatus?: "pending" | "accepted" | "denied" | null;
  onConfirm?: () => void;
  onCancel?: () => void;
  acting?: boolean;
  bartenderConfirmed?: boolean;
  distanceMiles?: number | null;
  earlyAccess?: boolean;
  bestMatch?: boolean;
  booking?: Booking | null;
  onArrivalConfirmed?: () => void;
  venueName?: string | null;
  venueVerified?: boolean;
  rtwBlocked?: boolean;
  rtwBlockedLabel?: string;
  rtwBlockedReason?: string;
  workedHereCount?: number;
}) {
  const start = new Date(shift.start_time);
  const end = new Date(shift.end_time);
  const rate = getPayRate(shift.role_type);
  const bartenderRate = rate.bartender_pence / 100;
  const roleLabel = rate.label;

  const hours = (end.getTime() - start.getTime()) / 3_600_000;
  const total = Math.round(bartenderRate * hours * 100) / 100;
  const canConfirm = shift.status === "paid" && end.getTime() <= Date.now() && !bartenderConfirmed;
  const canCancel = ["open", "awaiting_payment", "paid"].includes(shift.status) && start.getTime() > Date.now();
  // No-show reports are venue-only — bartenders cannot report the venue.
  const inArrivalWindow = shift.status === "paid" && start.getTime() - Date.now() <= 2 * 3_600_000 && end.getTime() >= Date.now();
  const arrivalConfirmFn = useServerFn(confirmArrival);
  const [arrivalBusy, setArrivalBusy] = useState(false);
  const [emergencyOpen, setEmergencyOpen] = useState(false);
  const [mutualOpen, setMutualOpen] = useState(false);
  const handleArrival = async () => {
    if (!booking) return;
    setArrivalBusy(true);
    try {
      const res = await arrivalConfirmFn({ data: { booking_id: booking.id } });
      toast.success(res.bothConfirmed ? "Both sides confirmed — payment guaranteed." : "Arrival recorded. Awaiting venue.");
      onArrivalConfirmed?.();
    } catch (err) { toast.error(err instanceof Error ? err.message : "Could not confirm arrival."); }
    finally { setArrivalBusy(false); }
  };
  const bothArrived = !!(booking?.arrival_venue_confirmed_at && booking?.arrival_bartender_confirmed_at);
  // Emergency stop: only while the shift is active (not completed/cancelled)
  // and within the live window (from 6h before start through the shift end).
  const canRaiseIncident = !!booking
    && ["paid", "awaiting_payment", "filled", "flagged_for_review"].includes(shift.status)
    && start.getTime() - Date.now() <= 6 * 3_600_000
    && end.getTime() >= Date.now();



  return (
    <article className="border border-border bg-card p-6 flex items-start justify-between gap-6 flex-wrap">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-3 flex-wrap">
          <p className="font-display text-xl">
            {start.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" })}
          </p>
          {shift.requires_dbs && (
            <span className="border border-accent text-accent px-2 py-0.5 tracking-wide-luxury text-[0.6rem]">DBS Required</span>
          )}
          {earlyAccess && (
            <span className="border border-accent text-accent px-2 py-0.5 tracking-wide-luxury text-[0.6rem]">★ Early Access</span>
          )}
          {bestMatch && !bookingStatus && (
            <span className="border border-emerald-600 bg-emerald-600/10 text-emerald-700 px-2 py-0.5 tracking-wide-luxury text-[0.6rem]">★ Best Match</span>
          )}
          {(workedHereCount ?? 0) > 0 && (
            <span
              className="border border-emerald-700 bg-emerald-50 text-emerald-800 px-2 py-0.5 tracking-wide-luxury text-[0.6rem]"
              title="One-tap reapply — you've worked here before"
            >
              ↻ Reapply · Worked here {workedHereCount}×
            </span>
          )}
          {shift.status === "open" && (
            <span className="ml-auto"><SaveShiftButton shiftId={shift.id} compact /></span>
          )}
        </div>
        <p className="mt-1 text-sm text-muted-foreground">
          {start.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })} — {end.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}
        </p>
        {venueName && (
          <p className="mt-2 text-sm font-medium flex items-center gap-2 flex-wrap">
            {venueName}
            {venueVerified && (
              <span className="border border-emerald-700 text-emerald-700 px-1.5 py-0.5 tracking-wide-luxury text-[0.55rem]">✓ Verified business</span>
            )}
            {shift.venue_id && (
              <FollowVenueButton venueId={shift.venue_id} venueName={venueName} />
            )}
          </p>
        )}
        {booking && start.getTime() <= Date.now() && end.getTime() >= Date.now() && (
          <span className="mt-2 inline-block border border-emerald-600 text-emerald-700 px-2 py-0.5 tracking-wide-luxury text-[0.6rem]">LIVE NOW</span>
        )}
        <div className="mt-3">
          <p className="tracking-wide-luxury text-[0.6rem] text-muted-foreground">Your Rate</p>
          <p className="font-display text-lg text-foreground/70">£{bartenderRate.toFixed(2)}<span className="text-xs text-muted-foreground">/hr</span></p>
          <p className="text-[0.65rem] text-muted-foreground">Engagement total · £{total.toFixed(2)}</p>
        </div>
        {(shift.address || shift.postcode) && (
          <p className="mt-3 text-xs text-muted-foreground">
            📍 {[shift.address, shift.postcode].filter(Boolean).join(" · ")}
            {distanceMiles != null && (
              <span className="ml-2 tracking-wide-luxury text-accent">{distanceMiles.toFixed(1)} mi away</span>
            )}
          </p>
        )}
        {shift.notes && <ShiftBriefText text={shift.notes} />}
        {shift.status === "awaiting_payment" && (
          <p className="mt-3 text-[0.7rem] tracking-wide-luxury text-amber-700">Awaiting venue payment</p>
        )}
        {shift.status === "paid" && (
          <p className="mt-3 text-[0.7rem] tracking-wide-luxury text-emerald-700">
            Paid · {bartenderConfirmed ? "You've confirmed. Awaiting venue confirmation." : end.getTime() > Date.now() ? "Confirm completion after shift end" : "Ready to confirm"}
          </p>
        )}
        {booking?.actual_arrival_at && (() => {
          const startTs = start.getTime();
          const arrTs = new Date(booking.actual_arrival_at as string).getTime();
          const lateMin = Math.max(0, Math.round((arrTs - startTs) / 60000));
          const ded = (booking.late_deduction_pence ?? 0) / 100;
          const perHr = lateMin > 0 ? Math.round((ded / lateMin) * 60 * 100) / 100 : bartenderRate;
          return (
            <div className="mt-4 border border-border bg-background/60 p-3 text-[0.7rem] tracking-wide-luxury">
              <p className="uppercase text-accent">Arrival recorded by venue</p>
              <dl className="mt-1 grid grid-cols-2 gap-x-3 gap-y-1 text-muted-foreground">
                <dt>Scheduled start</dt><dd className="text-foreground/90 text-right">{start.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}</dd>
                <dt>Logged arrival</dt><dd className="text-foreground/90 text-right">{new Date(booking.actual_arrival_at as string).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}</dd>
                <dt>Minutes late</dt><dd className="text-foreground/90 text-right">{lateMin} min</dd>
                {lateMin > 0 && (<>
                  <dt>Pro-rata rate</dt><dd className="text-foreground/90 text-right">≈ £{perHr.toFixed(2)}/hr</dd>
                  <dt>Deducted from payout</dt><dd className="text-destructive font-medium text-right">−£{ded.toFixed(2)}</dd>
                </>)}
              </dl>
              <div className="mt-3">
                <DisputeArrivalButton
                  bookingId={booking.id}
                  loggedArrivalAt={booking.actual_arrival_at as string}
                  loggedAt={booking.late_arrival_logged_at ?? null}
                  alreadyDisputedAt={booking.arrival_disputed_at ?? null}
                  resolution={booking.arrival_dispute_resolution ?? null}
                  onChanged={() => onArrivalConfirmed?.()}
                />
              </div>
            </div>
          );
        })()}
      </div>
      <div className="flex flex-col items-end gap-2 shrink-0">
        {onRegister ? (
          bookingStatus === "pending" ? (
            <span className="border border-accent text-accent px-3 py-1 tracking-wide-luxury text-[0.65rem]">Interest Registered</span>
          ) : bookingStatus === "denied" ? (
            <span className="border border-border text-muted-foreground px-3 py-1 tracking-wide-luxury text-[0.65rem]">Not Selected</span>
          ) : rtwBlocked ? (
            <div className="flex flex-col items-end gap-1.5 max-w-[14rem]">
              <Button
                onClick={onRegister}
                aria-disabled="true"
                title={rtwBlockedReason}
                className="rounded-none bg-muted text-muted-foreground hover:bg-muted tracking-wide-luxury cursor-not-allowed"
              >
                Right to Work · {rtwBlockedLabel}
              </Button>
              <p className="text-[0.6rem] text-destructive text-right leading-snug">
                {rtwBlockedReason}
              </p>
            </div>
          ) : (
            <Button onClick={onRegister} disabled={registering} className="rounded-none bg-accent text-primary-foreground hover:bg-accent/90 tracking-wide-luxury">
              {registering ? "Sending…" : "Register Interest"}
            </Button>
          )
        ) : (
          <>
            {booking && (
              <Link
                to="/shift/$id/notes"
                params={{ id: shift.id }}
                className="border border-border px-3 py-1 tracking-wide-luxury text-[0.65rem] hover:border-accent text-center"
              >
                Message Venue →
              </Link>
            )}
            {booking && end.getTime() > Date.now() && (
              <button
                type="button"
                onClick={() => {
                  downloadIcs(`shift-${shift.id}`, {
                    uid: shift.id,
                    title: `${roleLabel} · ${venueName ?? "Hospitality Reserve engagement"}`,
                    startIso: shift.start_time,
                    endIso: shift.end_time,
                    location: [shift.address, shift.postcode].filter(Boolean).join(", ") || null,
                    description: `Engagement via The Hospitality Reserve. Rate £${bartenderRate.toFixed(2)}/hr.${shift.notes ? `\n\nVenue notes: ${shift.notes}` : ""}`,
                    url: "https://thehospitalityreserve.com/bartender",
                  });
                }}
                className="border border-border px-3 py-1 tracking-wide-luxury text-[0.65rem] hover:border-accent text-center"
              >
                Add to Calendar
              </button>
            )}
            {booking && ["awaiting_payment", "paid"].includes(shift.status) && (
              <Link
                to="/bartender/booking-confirmation"
                search={{ shift_id: shift.id }}
                className="border border-accent px-3 py-1 tracking-wide-luxury text-[0.65rem] text-accent hover:bg-accent hover:text-accent-foreground text-center"
              >
                View Confirmation →
              </Link>
            )}

            {canRaiseIncident && (
              <div className="flex flex-col items-stretch gap-1.5 border-t border-border pt-2 mt-1 w-full">
                <p className="text-[0.6rem] tracking-wide-luxury text-muted-foreground uppercase">During shift</p>
                <Button
                  onClick={() => setMutualOpen(true)}
                  variant="outline"
                  className="rounded-none border-accent text-accent hover:bg-accent hover:text-accent-foreground tracking-wide-luxury text-[0.65rem] w-full"
                >
                  🤝 Mutual Finish / Unforeseen Event
                </Button>
                <p className="text-[0.55rem] text-muted-foreground leading-tight">
                  Quiet night or unexpected situation? Both agree to finish early. Prorated pay.
                </p>
                <Button
                  onClick={() => setEmergencyOpen(true)}
                  variant="outline"
                  className="rounded-none border-destructive text-destructive hover:bg-destructive hover:text-background tracking-wide-luxury text-[0.65rem] w-full mt-2"
                >
                  🚩 Raise a Concern
                </Button>
                <p className="text-[0.55rem] text-muted-foreground leading-tight">
                  Something went wrong. Wages are frozen pending our immediate review.
                </p>
                <MutualFinishDialog
                  shiftId={shift.id}
                  shiftStart={shift.start_time}
                  shiftEnd={shift.end_time}
                  open={mutualOpen}
                  onOpenChange={setMutualOpen}
                  onSubmitted={onArrivalConfirmed}
                />
                <EndShiftEarlyDialog
                  shiftId={shift.id}
                  open={emergencyOpen}
                  onOpenChange={setEmergencyOpen}
                  onSubmitted={onArrivalConfirmed}
                />
                <MutualFinishBanner shiftId={shift.id} onChanged={onArrivalConfirmed} />
              </div>

            )}


            {booking && inArrivalWindow && (
              <div className="text-right">
                <Button
                  onClick={handleArrival}
                  disabled={arrivalBusy || !!booking.arrival_bartender_confirmed_at}
                  className="rounded-none bg-foreground text-background hover:bg-foreground/90 tracking-wide-luxury"
                >
                  {booking.arrival_bartender_confirmed_at ? "Arrival Confirmed" : arrivalBusy ? "…" : "I've Arrived"}
                </Button>
                {bothArrived && (
                  <p className="mt-1 text-[0.65rem] tracking-wide-luxury text-emerald-700">✓ Payment guaranteed</p>
                )}
                {!bothArrived && booking.arrival_bartender_confirmed_at && (
                  <p className="mt-1 text-[0.65rem] tracking-wide-luxury text-muted-foreground">Awaiting venue</p>
                )}
              </div>
            )}
            {canConfirm && onConfirm && booking && (
              <div className="w-full">
                <ShiftCompletionEvidence bookingId={booking.id} bartenderId={shift.bartender_id ?? ""} />
                <Button onClick={onConfirm} disabled={acting} className="rounded-none bg-accent text-primary-foreground hover:bg-accent/90 tracking-wide-luxury mt-2 w-full">
                  {acting ? "…" : "Confirm Completion"}
                </Button>
              </div>
            )}

            {canCancel && onCancel && (
              <Button onClick={onCancel} disabled={acting} variant="outline" className="rounded-none border-border tracking-wide-luxury text-xs">
                Cancel
              </Button>
            )}
            {/* No-show reports are venue-only */}
            {!canConfirm && !canCancel && (
              <span className="border border-foreground text-foreground px-3 py-1 tracking-wide-luxury text-[0.65rem]">
                {shift.status === "completed" ? "Completed" : shift.status === "cancelled" ? "Cancelled" : shift.status === "disputed" ? "Disputed" : "Confirmed"}
              </span>
            )}
            {booking && <ShiftTimelineButton shiftId={shift.id} label="Timeline" />}
          </>
        )}
      </div>
    </article>
  );
}
